import React, { useState, useCallback, useMemo } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Divider,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import {
  commonButtonStyles,
  autoFillButtonStyles,
  PaperBoxStyles,
} from '../../../../components/styles/common-style';
import { lookupPostalCode, isValidPostalCodeFormat } from '../../../../lib/postal-code-service';

import type { FactoryAddress, FactoryContact, ContactRoleType } from '../../types/company.types';

import { CONTACT_ROLE_TYPES } from '@/lib/constants';
import { getStylesForEditableField } from '@/utils/helpers';

interface FactorySectionProps {
  factoryAddress?: FactoryAddress;
  originalFactoryAddress?: FactoryAddress;
  factoryContacts: FactoryContact[];
  originalFactoryContacts?: FactoryContact[];
  isEditMode?: boolean;
  onUpdateFactoryAddress: (field: keyof FactoryAddress, value: string) => void;
  onAddFactoryContact: () => void;
  onUpdateFactoryContact: (index: number, field: keyof FactoryContact, value: string) => void;
  onRemoveFactoryContact: (index: number) => void;
  getFieldError: (field: string) => string | undefined;
  isFieldValid: (field: string) => boolean;
}

export const FactorySection: React.FC<FactorySectionProps> = ({
  factoryAddress,
  originalFactoryAddress,
  factoryContacts,
  originalFactoryContacts,
  isEditMode,
  onUpdateFactoryAddress,
  onAddFactoryContact,
  onUpdateFactoryContact,
  onRemoveFactoryContact,
  getFieldError,
  isFieldValid,
}) => {
  const contactRoleTypes = CONTACT_ROLE_TYPES;
  const [isLookingUp, setIsLookingUp] = useState(false);

  // Provide default values if factoryAddress is undefined
  const safeFactoryAddress = useMemo(
    () =>
      factoryAddress || {
        factoryName: '',
        postalCode1: '',
        postalCode2: '',
        addressLocation: '',
        phone: '',
        fax: '',
      },
    [factoryAddress]
  );

  // Auto-fill address when postal code is complete
  const handlePostalCodeLookup = useCallback(async () => {
    const { postalCode1, postalCode2 } = safeFactoryAddress;

    if (!isValidPostalCodeFormat(postalCode1, postalCode2)) {
      return;
    }

    setIsLookingUp(true);
    try {
      const result = await lookupPostalCode(postalCode1, postalCode2);
      if (result) {
        onUpdateFactoryAddress('addressLocation', result.fullAddress);
      }
    } catch (error) {
      console.error('Postal code lookup failed:', error);
    } finally {
      setIsLookingUp(false);
    }
  }, [safeFactoryAddress, onUpdateFactoryAddress]);

  // Handle postal code change with auto-lookup
  const handlePostalCodeChange = useCallback(
    (field: 'postalCode1' | 'postalCode2', value: string) => {
      onUpdateFactoryAddress(field, value);

      // Trigger lookup when both codes are complete
      setTimeout(() => {
        const updatedCode1 = field === 'postalCode1' ? value : safeFactoryAddress.postalCode1;
        const updatedCode2 = field === 'postalCode2' ? value : safeFactoryAddress.postalCode2;

        if (isValidPostalCodeFormat(updatedCode1, updatedCode2)) {
          void handlePostalCodeLookup();
        }
      }, 100);
    },
    [
      onUpdateFactoryAddress,
      safeFactoryAddress.postalCode1,
      safeFactoryAddress.postalCode2,
      handlePostalCodeLookup,
    ]
  );

  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 2, paddingBottom: '40px' }}>
      <Typography
        variant='h5'
        fontWeight='bold'
        sx={{
          mb: 3,
          color: '#1A1C20',
          fontSize: '22px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        工場情報
      </Typography>

      {/* Factory Address */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          sx={{
            color: '#1A1C20',
            fontSize: '16px',
            fontWeight: 500,
            mb: 2,
          }}
        >
          工場
        </Typography>

        <Grid container spacing={3}>
          {/* 工場名 */}
          {/* @ts-expect-error MUI v7 Grid typing issue */}
          <Grid item xs={12} sx={{ width: '100%' }}>
            <TextField
              label='工場名'
              placeholder='工場を入力'
              value={safeFactoryAddress.factoryName}
              onChange={(e) => onUpdateFactoryAddress('factoryName', e.target.value)}
              error={!isFieldValid('factoryAddress.factoryName')}
              helperText={getFieldError('factoryAddress.factoryName')}
              fullWidth
              slotProps={{
                input: {
                  inputProps: {
                    maxLength: 50,
                  },
                  sx: getStylesForEditableField(
                    !!isEditMode,
                    originalFactoryAddress?.factoryName,
                    safeFactoryAddress.factoryName
                  ),
                },
              }}
            />
          </Grid>

          {/* Postal Code */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start', width: '100%' }}>
            <TextField
              sx={{ width: '40%' }}
              label='郵便番号（前半）'
              placeholder='3桁'
              value={safeFactoryAddress.postalCode1}
              onChange={(e) => handlePostalCodeChange('postalCode1', e.target.value)}
              error={!isFieldValid('factoryAddress.postalCode1')}
              helperText={getFieldError('factoryAddress.postalCode1')}
              slotProps={{
                input: {
                  inputProps: {
                    maxLength: 3,
                    pattern: '[0-9]*',
                    inputMode: 'numeric',
                  },
                  sx: getStylesForEditableField(
                    !!isEditMode,
                    originalFactoryAddress?.postalCode1,
                    safeFactoryAddress.postalCode1
                  ),
                },
              }}
            />
            <TextField
              sx={{ width: '40%' }}
              label='郵便番号（後半）'
              placeholder='4桁'
              value={safeFactoryAddress.postalCode2}
              onChange={(e) => handlePostalCodeChange('postalCode2', e.target.value)}
              error={!isFieldValid('factoryAddress.postalCode2')}
              helperText={getFieldError('factoryAddress.postalCode2')}
              slotProps={{
                input: {
                  inputProps: {
                    maxLength: 4,
                    pattern: '[0-9]*',
                    inputMode: 'numeric',
                  },
                  sx: getStylesForEditableField(
                    !!isEditMode,
                    originalFactoryAddress?.postalCode2,
                    safeFactoryAddress.postalCode2
                  ),
                },
              }}
            />
            <Button
              variant='outlined'
              onClick={() => {
                void handlePostalCodeLookup();
              }}
              disabled={
                !isValidPostalCodeFormat(
                  safeFactoryAddress.postalCode1,
                  safeFactoryAddress.postalCode2
                ) || isLookingUp
              }
              startIcon={isLookingUp ? <CircularProgress size={16} /> : <SearchIcon />}
              sx={{ ...autoFillButtonStyles, width: '20%' }}
            >
              {isLookingUp ? '検索中...' : '住所自動入力'}
            </Button>
          </Box>

          {/* Address Location */}
          {/* @ts-expect-error MUI v7 Grid typing issue */}
          <Grid item xs={12} sx={{ width: '100%' }}>
            <TextField
              label='工場所在地'
              placeholder='工場所在地を入力'
              value={safeFactoryAddress.addressLocation}
              onChange={(e) => onUpdateFactoryAddress('addressLocation', e.target.value)}
              error={!isFieldValid('factoryAddress.addressLocation')}
              helperText={getFieldError('factoryAddress.addressLocation')}
              fullWidth
              slotProps={{
                input: {
                  inputProps: {
                    maxLength: 100,
                  },
                  sx: getStylesForEditableField(
                    !!isEditMode,
                    originalFactoryAddress?.addressLocation,
                    safeFactoryAddress.addressLocation
                  ),
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Factory Contacts */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography
            variant='h6'
            sx={{ color: '#1A1C20', fontSize: '16px', fontWeight: 500, mb: 2 }}
          >
            連絡責任者（最大10名）
          </Typography>
        </Box>

        {factoryContacts.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: 'text.secondary',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <PersonIcon sx={{ fontSize: 48, mb: 1, color: '#0047BA' }} />
            <Typography variant='body1'>連絡責任者</Typography>
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={onAddFactoryContact}
              disabled={factoryContacts.length >= 10}
              sx={{
                ...commonButtonStyles,
                mt: 2,
                px: 3,
                py: 1,
              }}
            >
              担当者追加
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {factoryContacts.map((contact, index) => {
              const originalFactoryContactItem =
                contact.index !== undefined ? originalFactoryContacts?.[contact.index] : undefined;
              return (
                <Paper key={contact.id} variant='outlined' sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <Typography variant='subtitle1' fontWeight={600}>
                      担当者 {index + 1}
                    </Typography>
                    <IconButton
                      color='error'
                      onClick={() => onRemoveFactoryContact(index)}
                      size='small'
                      sx={{ color: '#FF8985' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  {/* First Row - 区分, 部署, 担当者 */}
                  <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
                    {/* @ts-expect-error MUI v7 Grid typing issue */}
                    <Grid item xs={12} md={4} sx={{ width: '30%' }}>
                      <FormControl
                        fullWidth
                        error={!isFieldValid(`factoryContacts.${index}.roleType`)}
                      >
                        <InputLabel>区分</InputLabel>
                        <Select
                          value={contact.roleType}
                          onChange={(e) =>
                            onUpdateFactoryContact(
                              index,
                              'roleType',
                              e.target.value as ContactRoleType
                            )
                          }
                          label='区分'
                          slotProps={{
                            input: {
                              sx: getStylesForEditableField(
                                !!isEditMode,
                                originalFactoryContactItem?.roleType,
                                contact.roleType
                              ),
                            },
                          }}
                        >
                          {contactRoleTypes.map((role) => (
                            <MenuItem key={role} value={role}>
                              {role}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {getFieldError(`factoryContacts.${index}.roleType`)}
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    {/* @ts-expect-error MUI v7 Grid typing issue */}
                    <Grid item xs={12} md={4} sx={{ width: '30%' }}>
                      <TextField
                        label='部署'
                        placeholder='部署を入力'
                        value={contact.department}
                        onChange={(e) =>
                          onUpdateFactoryContact(index, 'department', e.target.value)
                        }
                        error={!isFieldValid(`factoryContacts.${index}.department`)}
                        helperText={getFieldError(`factoryContacts.${index}.department`)}
                        fullWidth
                        slotProps={{
                          input: {
                            inputProps: {
                              maxLength: 50,
                            },
                            sx: getStylesForEditableField(
                              !!isEditMode,
                              originalFactoryContactItem?.department,
                              contact.department
                            ),
                          },
                        }}
                      />
                    </Grid>

                    {/* @ts-expect-error MUI v7 Grid typing issue */}
                    <Grid item xs={12} md={4} sx={{ width: '30%' }}>
                      <TextField
                        label='担当者'
                        placeholder='担当者を入力'
                        value={contact.contactPerson}
                        onChange={(e) =>
                          onUpdateFactoryContact(index, 'contactPerson', e.target.value)
                        }
                        error={!isFieldValid(`factoryContacts.${index}.contactPerson`)}
                        helperText={getFieldError(`factoryContacts.${index}.contactPerson`)}
                        fullWidth
                        slotProps={{
                          input: {
                            inputProps: {
                              maxLength: 50,
                            },
                            sx: getStylesForEditableField(
                              !!isEditMode,
                              originalFactoryContactItem?.contactPerson,
                              contact.contactPerson
                            ),
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Second Row - 電話番号, メールアドレス, FAX番号 */}
                  <Grid container spacing={2} sx={{ width: '100%' }}>
                    {/* @ts-expect-error MUI v7 Grid typing issue */}
                    <Grid item xs={12} md={4} sx={{ width: '30%' }}>
                      <TextField
                        label='電話番号'
                        placeholder='電話番号を入力'
                        value={contact.phone}
                        onChange={(e) => onUpdateFactoryContact(index, 'phone', e.target.value)}
                        error={!isFieldValid(`factoryContacts.${index}.phone`)}
                        helperText={getFieldError(`factoryContacts.${index}.phone`)}
                        fullWidth
                        slotProps={{
                          input: {
                            inputProps: {
                              maxLength: 20,
                            },
                            sx: getStylesForEditableField(
                              !!isEditMode,
                              originalFactoryContactItem?.phone,
                              contact.phone
                            ),
                          },
                        }}
                      />
                    </Grid>

                    {/* @ts-expect-error MUI v7 Grid typing issue */}
                    <Grid item xs={12} md={4} sx={{ width: '30%' }}>
                      <TextField
                        label='メールアドレス'
                        placeholder='メールアドレスを入力'
                        value={contact.email}
                        onChange={(e) => onUpdateFactoryContact(index, 'email', e.target.value)}
                        error={!isFieldValid(`factoryContacts.${index}.email`)}
                        helperText={getFieldError(`factoryContacts.${index}.email`)}
                        fullWidth
                        type='email'
                        slotProps={{
                          input: {
                            inputProps: {
                              maxLength: 100,
                            },
                            sx: getStylesForEditableField(
                              !!isEditMode,
                              originalFactoryContactItem?.email,
                              contact.email
                            ),
                          },
                        }}
                      />
                    </Grid>

                    {/* @ts-expect-error MUI v7 Grid typing issue */}
                    <Grid item xs={12} md={4} sx={{ width: '30%' }}>
                      <TextField
                        label='FAX番号'
                        placeholder='FAX番号を入力'
                        value={contact.fax}
                        onChange={(e) => onUpdateFactoryContact(index, 'fax', e.target.value)}
                        error={!isFieldValid(`factoryContacts.${index}.fax`)}
                        helperText={getFieldError(`factoryContacts.${index}.fax`)}
                        fullWidth
                        slotProps={{
                          input: {
                            inputProps: {
                              maxLength: 20,
                            },
                            sx: getStylesForEditableField(
                              !!isEditMode,
                              originalFactoryContactItem?.fax,
                              contact.fax
                            ),
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Add Factory Contact Button (bottom) */}
        {factoryContacts.length > 0 && factoryContacts.length < 10 && (
          <Box sx={{ textAlign: 'center', mt: 3, mb: 4 }}>
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              onClick={onAddFactoryContact}
              sx={{
                ...commonButtonStyles,
                px: 3,
                py: 1,
              }}
            >
              担当者追加 ({factoryContacts.length}/10)
            </Button>
          </Box>
        )}
      </Box>
    </PaperBoxStyles>
  );
};
